import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star1 } from "iconsax-react";
import { Spin } from "antd";
import DBSelect from "../../../component/global/DBSelect";
import { useRequest } from "../../../hooks/helperData/useRequest";
import {
    REPORT_SCORE_BREAKDOWN,
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_TREE,
} from "../../../helperData/ApiAddress";

const ALL = "all";

function flattenItemTypes(nodes, prefix = "") {
    const options = [];
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        const label = prefix ? `${prefix} / ${node.name}` : node.name;
        options.push({ value: node.id, label, node });
        if (node.children?.length) {
            options.push(...flattenItemTypes(node.children, label));
        }
    });
    return options;
}

function collectTypeIds(node) {
    const ids = [node.id];
    (node.children || []).forEach((child) => {
        ids.push(...collectTypeIds(child));
    });
    return ids;
}

function formatScore(value, locale) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return Number(value).toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
}

function toneForScore(value) {
    if (value == null) return "slate";
    if (value >= 4) return "teal";
    if (value >= 3) return "amber";
    return "rose";
}

function ScoreStat({ tone, label, value, hint, locale, outOf }) {
    const width =
        value == null
            ? 0
            : Math.max(0, Math.min(100, (Number(value) / 5) * 100));
    return (
        <article className={`score-stat score-stat--${tone}`}>
            <p className="score-stat__label">{label}</p>
            <div className="score-stat__value">
                <span>{formatScore(value, locale)}</span>
                <small>{outOf}</small>
            </div>
            <div className="score-stat__track" aria-hidden="true">
                <div style={{ width: `${width}%` }} />
            </div>
            <p className="score-stat__hint">{hint}</p>
        </article>
    );
}

export default function CriteriaScoreBreakdown() {
    const { t, i18n } = useTranslation();
    const locale = i18n.language?.startsWith("fa") ? "fa-IR" : "en-US";
    const [itemType, setItemType] = useState(ALL);
    const [item, setItem] = useState(ALL);
    const [typeTree, setTypeTree] = useState([]);
    const [items, setItems] = useState([]);
    const [breakdown, setBreakdown] = useState(null);

    const [getTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getBreakdown, loading] = useRequest(REPORT_SCORE_BREAKDOWN, "GET");

    useEffect(() => {
        getTypes()
            .then((tree) =>
                setTypeTree(Array.isArray(tree) ? tree : tree?.children || [])
            )
            .catch(() => setTypeTree([]));
    }, []);

    const typeOptions = useMemo(
        () => flattenItemTypes(typeTree),
        [typeTree]
    );
    const selectedType = typeOptions.find((option) => option.value === itemType);

    useEffect(() => {
        const params = {};
        if (selectedType?.node) {
            params.item_type_in = collectTypeIds(selectedType.node).join(",");
        }
        getItems(params)
            .then((rows) => {
                const list = Array.isArray(rows) ? rows : rows?.results || [];
                setItems(
                    list.map((row) => ({ value: row.id, label: row.name }))
                );
            })
            .catch(() => setItems([]));
    }, [itemType, typeTree]);

    useEffect(() => {
        const params = {};
        if (itemType !== ALL) params.item_type = itemType;
        if (item !== ALL) params.item = item;
        getBreakdown(params)
            .then((payload) => setBreakdown(payload || null))
            .catch(() => setBreakdown(null));
    }, [itemType, item]);

    const criteria = breakdown?.criteria || [];
    const hasScores =
        Number(breakdown?.criteria_response_count) > 0 ||
        Number(breakdown?.overall_response_count) > 0;
    const outOf = t("score.breakdown.outOf");

    return (
        <section
            className="score-breakdown report-card"
            aria-labelledby="score-breakdown-title"
        >
            <div className="score-breakdown__head">
                <div className="score-breakdown__intro">
                    <h2 id="score-breakdown-title" className="report-header">
                        <Star1 size={18} variant="Bold" aria-hidden="true" />
                        {t("score.breakdown.title")}
                    </h2>
                    <p>{t("score.breakdown.subtitle")}</p>
                </div>
                <div className="score-breakdown__filters">
                    <label>
                        <span>{t("score.breakdown.itemType")}</span>
                        <DBSelect
                            size="large"
                            showSearch
                            optionFilterProp="label"
                            value={itemType}
                            onChange={(value) => {
                                setItemType(value ?? ALL);
                                setItem(ALL);
                            }}
                            options={[
                                {
                                    value: ALL,
                                    label: t("score.breakdown.all"),
                                },
                                ...typeOptions.map(({ value, label }) => ({
                                    value,
                                    label,
                                })),
                            ]}
                        />
                    </label>
                    <label>
                        <span>{t("score.breakdown.item")}</span>
                        <DBSelect
                            size="large"
                            showSearch
                            optionFilterProp="label"
                            value={item}
                            onChange={(value) => setItem(value ?? ALL)}
                            options={[
                                {
                                    value: ALL,
                                    label: t("score.breakdown.all"),
                                },
                                ...items,
                            ]}
                        />
                    </label>
                </div>
            </div>

            {loading && !breakdown ? (
                <div className="spin">
                    <Spin />
                </div>
            ) : !hasScores ? (
                <div className="score-breakdown__empty">
                    <p>{t("score.breakdown.empty")}</p>
                </div>
            ) : (
                <>
                    <div className="score-breakdown__stats">
                        <ScoreStat
                            tone={toneForScore(breakdown.criteria_average)}
                            label={t("score.breakdown.criteriaAverage")}
                            value={breakdown.criteria_average}
                            locale={locale}
                            outOf={outOf}
                            hint={t("score.breakdown.criteriaAverage.hint", {
                                count: breakdown.criteria_response_count || 0,
                            })}
                        />
                        <ScoreStat
                            tone={toneForScore(breakdown.overall_average)}
                            label={t("score.breakdown.overall")}
                            value={breakdown.overall_average}
                            locale={locale}
                            outOf={outOf}
                            hint={
                                breakdown.overall_average == null
                                    ? t("score.breakdown.overall.empty")
                                    : t("score.breakdown.overall.hint", {
                                          count:
                                              breakdown.overall_response_count ||
                                              0,
                                      })
                            }
                        />
                        <ScoreStat
                            tone={toneForScore(breakdown.combined_average)}
                            label={t("score.breakdown.combined")}
                            value={breakdown.combined_average}
                            locale={locale}
                            outOf={outOf}
                            hint={
                                breakdown.combined_average == null
                                    ? t("score.breakdown.overall.empty")
                                    : t("score.breakdown.combined.hint", {
                                          criteria: formatScore(
                                              breakdown.combined_criteria_average,
                                              locale
                                          ),
                                          overall: formatScore(
                                              breakdown.combined_overall_average,
                                              locale
                                          ),
                                          count:
                                              breakdown.combined_response_count ||
                                              0,
                                      })
                            }
                        />
                    </div>
                    <div className="score-breakdown__criteria">
                        <div className="score-breakdown__criteria-head">
                            <h3>{t("score.breakdown.byCriteria")}</h3>
                            <span>
                                {t("score.breakdown.responses", {
                                    count: breakdown.response_count || 0,
                                })}
                            </span>
                        </div>
                        {criteria.length === 0 ? (
                            <p className="score-breakdown__empty-inline">
                                {t("score.breakdown.empty")}
                            </p>
                        ) : (
                            <ul>
                                {criteria.map((row) => {
                                    const width = Math.max(
                                        0,
                                        Math.min(
                                            100,
                                            (Number(row.average) / 5) * 100
                                        )
                                    );
                                    return (
                                        <li key={row.id}>
                                            <div className="score-breakdown__row-top">
                                                <span>{row.name}</span>
                                                <strong>
                                                    {formatScore(
                                                        row.average,
                                                        locale
                                                    )}
                                                    <small>{outOf}</small>
                                                </strong>
                                            </div>
                                            <div
                                                className={`score-breakdown__bar score-breakdown__bar--${toneForScore(
                                                    row.average
                                                )}`}
                                                role="meter"
                                                aria-valuemin={0}
                                                aria-valuemax={5}
                                                aria-valuenow={Number(
                                                    row.average
                                                )}
                                                aria-label={row.name}
                                            >
                                                <div
                                                    style={{
                                                        width: `${width}%`,
                                                    }}
                                                />
                                            </div>
                                            <p>
                                                {t("score.breakdown.replies", {
                                                    count: row.count || 0,
                                                })}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </section>
    );
}
